import type React from "react";
import { useState } from "react";
import { Box, Drawer, Typography, Button, IconButton, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { motion, AnimatePresence } from "framer-motion";
import CustomTextField from "../../../../components/ui/Textfield";
import { ChromePicker } from "react-color";
import { tokens } from "../../../../theme";

interface AddCategorySidebarProps {
  open: boolean;
  onClose: () => void;
  onSave: (name: string, color: string) => void;
}

export const AddCategorySidebar: React.FC<AddCategorySidebarProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [categoryName, setCategoryName] = useState("");
  const [color, setColor] = useState("#1890FF");

  const handleSave = () => {
    onSave(categoryName, color);
    setCategoryName("");
    setColor("#1890FF");
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 420,
          p: 4,
        },
      }}
    >
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 100, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 4,
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Add design category
              </Typography>
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <CustomTextField
                fullWidth
                placeholder="Category name"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                variant="outlined"
              />
              <Box
                sx={{
                  border: `1px solid ${colors.primary[300]}`,
                  borderRadius: 2,
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    mb: 2,
                    color: "text.secondary",
                    alignSelf: "flex-start",
                  }}
                >
                  Pick color
                </Typography>

                <ChromePicker
                  color={color}
                  onChange={(c) => setColor(c.hex)}
                  disableAlpha
                  styles={{
                    default: {
                      picker: {
                        boxShadow: "none",
                        borderRadius: 8,
                        border: `1px solid ${colors.primary[300]}`,
                        padding: 4,
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={!color.trim()}
                  sx={{
                    mt: "auto",
                    py: 1.5,
                    width: "119px",
                    borderRadius: "8px",
                  }}
                >
                  Save
                </Button>
              </Box>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Drawer>
  );
};
